import { useState, useEffect, useRef } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useMediaQuery } from '../hooks/useMediaQuery'

// Floating in-app note widget. Click the icon → textarea slides up →
// type a note → submit. Each note captures the current URL + page title
// so Ben can review them later (or have Claude query them via the
// feedback_notes table).
//
// Keyboard: Cmd/Ctrl+. opens the panel, Esc closes, Cmd/Ctrl+Enter submits.
export function FeedbackWidget() {
  const { user } = useUser()
  const isMobile = useMediaQuery('(max-width: 1023px)')
  // Lift FAB above the 56px BottomNav (+ safe-area inset) on mobile/tablet.
  const fabBottom = isMobile ? 'calc(56px + 14px + env(safe-area-inset-bottom, 0px))' : 18
  const panelBottom = isMobile ? 'calc(56px + 14px + 56px + env(safe-area-inset-bottom, 0px))' : 74
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const [status, setStatus] = useState(null) // 'sending' | 'sent' | 'error'
  const textareaRef = useRef(null)

  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey
      if (mod && e.key === '.') {
        e.preventDefault()
        setOpen((o) => !o)
      } else if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  useEffect(() => {
    if (open && textareaRef.current) textareaRef.current.focus()
  }, [open])

  const submit = async () => {
    const trimmed = note.trim()
    if (!trimmed || status === 'sending') return
    setStatus('sending')
    try {
      const r = await fetch('/api/feedback-notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note: trimmed,
          page_url: typeof window !== 'undefined' ? window.location.pathname + window.location.search + window.location.hash : null,
          page_label: typeof document !== 'undefined' ? document.title : null,
          user_id: user?.id ?? null,
        }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      setStatus('sent')
      setNote('')
      setTimeout(() => {
        setStatus(null)
        setOpen(false)
      }, 900)
    } catch {
      setStatus('error')
    }
  }

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      submit()
    }
  }

  return (
    <>
      {/* Floating FAB */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Quick note (Cmd/Ctrl + .)"
        aria-label="Open quick note"
        style={{
          position: 'fixed', right: 18, bottom: fabBottom, zIndex: 90,
          width: 44, height: 44, borderRadius: 22,
          background: open ? 'rgba(52,211,153,0.18)' : 'rgba(15,23,42,0.85)',
          color: open ? '#34d399' : '#e5e7eb',
          border: `1px solid ${open ? 'rgba(52,211,153,0.45)' : 'rgba(255,255,255,0.12)'}`,
          backdropFilter: 'blur(8px)',
          fontSize: 18, cursor: 'pointer',
          display: 'grid', placeItems: 'center',
          boxShadow: '0 6px 24px rgba(0,0,0,0.45)',
          transition: 'background 0.15s, border-color 0.15s',
        }}
      >📝</button>

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="Quick note"
          style={{
            position: 'fixed', right: 18, bottom: panelBottom, zIndex: 91,
            width: 340, maxWidth: 'calc(100vw - 36px)',
            background: '#0b1220',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 10,
            boxShadow: '0 14px 40px rgba(0,0,0,0.55)',
            padding: 12,
            fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 8,
          }}>
            <div style={{
              fontFamily: 'JetBrains Mono', fontSize: 9.5,
              letterSpacing: 1.6, color: '#94a3b8', fontWeight: 700,
            }}>QUICK NOTE</div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                background: 'transparent', border: 'none',
                color: '#94a3b8', cursor: 'pointer', fontSize: 14, padding: 0,
              }}
            >×</button>
          </div>
          <textarea
            ref={textareaRef}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="What's wrong / what's the idea?"
            rows={4}
            maxLength={2000}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 6, color: '#e5e7eb',
              padding: '8px 10px', fontSize: 13, lineHeight: 1.45,
              fontFamily: 'inherit', resize: 'vertical',
              outline: 'none', minHeight: 80,
            }}
          />
          <div style={{
            marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          }}>
            <span style={{
              fontFamily: 'JetBrains Mono', fontSize: 9, color: '#64748b', letterSpacing: 0.5,
              maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }} title={typeof window !== 'undefined' ? window.location.pathname : ''}>
              {typeof window !== 'undefined' ? window.location.pathname : ''}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {status === 'sent' && (
                <span style={{ fontSize: 11, color: '#34d399', fontWeight: 600 }}>Saved ✓</span>
              )}
              {status === 'error' && (
                <span style={{ fontSize: 11, color: '#f87171', fontWeight: 600 }}>Failed</span>
              )}
              <button
                onClick={submit}
                disabled={!note.trim() || status === 'sending'}
                style={{
                  background: !note.trim() ? 'rgba(52,211,153,0.12)' : 'rgba(52,211,153,0.22)',
                  border: '1px solid rgba(52,211,153,0.4)',
                  color: '#34d399', fontWeight: 700, fontSize: 11,
                  fontFamily: 'JetBrains Mono', letterSpacing: 1,
                  padding: '6px 12px', borderRadius: 5,
                  cursor: !note.trim() ? 'not-allowed' : 'pointer',
                  opacity: !note.trim() ? 0.6 : 1,
                }}
              >{status === 'sending' ? 'SAVING…' : 'SAVE'}</button>
            </div>
          </div>
          <div style={{
            marginTop: 8, fontFamily: 'JetBrains Mono', fontSize: 8.5,
            color: '#475569', letterSpacing: 0.5,
          }}>
            ⌘/Ctrl + . opens · ⌘/Ctrl + Enter saves · Esc closes
          </div>
        </div>
      )}
    </>
  )
}
